import { createTheme } from '@mui/material/styles';

/**
 * Cheddar Wallet Demo — "warm gold fintech" design tokens.
 *
 * Gold is reserved for primary actions and pending states; never for body text.
 * Teal appears only as the testnet status dot. Addresses and hashes are mono.
 */
export const brand = {
  gold: '#fda92d',
  goldHover: '#f09a1b',
  goldSoft: '#FED680',
  goldWash: '#FEF4D4',
  goldDeep: '#B66816',
  goldDeeper: '#8f5010',
  amber: '#FFAB00',

  ink: '#1C252E',
  body: '#454F5B',
  muted: '#637381',
  faint: '#8B94A0',

  canvas: '#FFFBF5',
  canvasSunk: '#FBF7F0',
  surface: '#FFFFFF',
  hairline: '#F1E7D8',
  hairlineSoft: '#F5EEE4',
  border: '#E7DECF',

  successBg: '#E9F9EF',
  successFg: '#137547',
  warnBg: '#FFF6DF',
  warnFg: '#8A6100',
  warnBorder: '#FFE2A8',
  errorBg: '#FFEDE9',
  errorFg: '#B7301B',
  infoBg: '#FEF4D4',
  infoFg: '#7A6320',

  teal: '#00B8D9',

  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  cardRadius: 22,
  controlRadius: 13,
  cardShadow: '0 12px 28px -22px rgba(28,37,46,.5)',
  goldShadow: '0 8px 18px -6px rgba(253,169,45,.7)',
} as const;

export const demoTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.gold,
      dark: brand.goldDeep,
      light: brand.goldSoft,
      contrastText: brand.ink,
    },
    secondary: {
      main: brand.ink,
      contrastText: '#FFFFFF',
    },
    success: { main: brand.successFg, light: brand.successBg },
    warning: { main: brand.amber, dark: brand.warnFg, light: brand.warnBg },
    error: { main: '#FF5630', dark: brand.errorFg, light: brand.errorBg },
    info: { main: brand.goldDeep, light: brand.goldWash },
    text: {
      primary: brand.ink,
      secondary: brand.muted,
      disabled: brand.faint,
    },
    divider: brand.hairline,
    background: {
      default: brand.canvas,
      paper: brand.surface,
    },
  },

  typography: {
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.045em' },
    h2: { fontWeight: 800, letterSpacing: '-0.045em' },
    h3: { fontWeight: 800, letterSpacing: '-0.04em' },
    h4: { fontWeight: 800, letterSpacing: '-0.035em' },
    h5: { fontWeight: 800, letterSpacing: '-0.03em' },
    h6: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 700 },
    subtitle2: { fontSize: '0.78125rem', fontWeight: 600, letterSpacing: '0.01em' },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.84375rem', lineHeight: 1.55 },
    caption: { fontSize: '0.78125rem', lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
  },

  shape: { borderRadius: 12 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.canvas,
          WebkitFontSmoothing: 'antialiased',
        },
        '@keyframes chd-pulse': {
          '0%,100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
        '@keyframes chd-shimmer': {
          '0%': { backgroundPosition: '-320px 0' },
          '100%': { backgroundPosition: '320px 0' },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: brand.controlRadius,
          fontWeight: 700,
        },
        sizeLarge: { height: 54, fontSize: '1rem', borderRadius: 14 },
        sizeMedium: { height: 48, fontSize: '0.9375rem' },
        sizeSmall: { height: 34, fontSize: '0.8125rem', borderRadius: 10, paddingInline: 14 },
        containedPrimary: {
          backgroundColor: brand.gold,
          color: brand.ink,
          boxShadow: brand.goldShadow,
          '&:hover': { backgroundColor: brand.goldHover, boxShadow: brand.goldShadow },
          '&.Mui-disabled': { backgroundColor: brand.goldSoft, color: 'rgba(28,37,46,.5)' },
        },
        containedSecondary: {
          backgroundColor: brand.ink,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#0F1519' },
        },
        outlined: {
          backgroundColor: brand.surface,
          borderColor: brand.border,
          color: brand.body,
          '&:hover': { backgroundColor: brand.canvasSunk, borderColor: brand.border },
        },
        text: {
          color: brand.goldDeep,
          '&:hover': { backgroundColor: brand.goldWash },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: brand.controlRadius,
          backgroundColor: brand.canvasSunk,
          '& fieldset': { borderColor: brand.border },
          '&:hover fieldset': { borderColor: brand.goldSoft },
          '&.Mui-focused': {
            backgroundColor: brand.surface,
            boxShadow: `0 0 0 4px ${brand.goldWash}`,
          },
          '&.Mui-focused fieldset': { borderWidth: 1.5, borderColor: brand.gold },
        },
        input: {
          fontSize: '1rem',
          color: brand.ink,
          '&::placeholder': { color: brand.faint, opacity: 1 },
        },
        notchedOutline: { transition: 'border-color .15s ease' },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.9375rem', color: brand.body, '&.Mui-focused': { color: brand.goldDeep } },
      },
    },

    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.75rem', marginLeft: 2, color: brand.faint } },
    },

    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: '1px solid transparent',
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          alignItems: 'flex-start',
        },
        standardSuccess: {
          backgroundColor: brand.successBg,
          color: brand.successFg,
          borderColor: '#C9EFD8',
          '& .MuiAlert-icon': { color: brand.successFg },
        },
        standardInfo: {
          backgroundColor: brand.goldWash,
          color: brand.infoFg,
          borderColor: '#F7E2AE',
          '& .MuiAlert-icon': { color: brand.goldDeep },
        },
        standardWarning: {
          backgroundColor: brand.warnBg,
          color: brand.warnFg,
          borderColor: brand.warnBorder,
          '& .MuiAlert-icon': { color: brand.amber },
        },
        standardError: {
          backgroundColor: brand.errorBg,
          color: brand.errorFg,
          borderColor: '#FFD6CD',
          '& .MuiAlert-icon': { color: brand.errorFg },
        },
        filledError: { backgroundColor: '#B7301B', color: '#FFFFFF' },
        message: { paddingTop: 6, paddingBottom: 6 },
        action: { paddingTop: 2 },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { gap: 8 },
        grouped: {
          border: `1px solid ${brand.border} !important`,
          borderRadius: '999px !important',
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          height: 34,
          paddingInline: 14,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: brand.body,
          backgroundColor: brand.surface,
          '&:hover': { backgroundColor: brand.canvasSunk },
          '&.Mui-selected': {
            backgroundColor: brand.goldWash,
            borderColor: `${brand.gold} !important`,
            color: brand.goldDeeper,
            fontWeight: 700,
            '&:hover': { backgroundColor: brand.goldWash },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontWeight: 600, fontSize: '0.8125rem' },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: brand.goldWash },
        },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'none' },
      styleOverrides: {
        root: {
          color: brand.goldDeep,
          fontWeight: 600,
          '&:hover': { color: brand.goldDeeper },
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: { root: { color: brand.gold } },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: brand.hairlineSoft } },
    },
  },
});
